import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch} from 'react-redux';
import * as actions from '../../Redux/Actions';
import { Badge, Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import MainCard from '../../component/MainCard/MainCard';
import TablePagination from '../../component/TablePagination/TablePagination';
import FilterValues from '../../component/FieldFilter/FilterValues';
import { showErrorToastMessage, showSuccessToastMessage } from '../../component/Toasts/Toasts';
import ReviewNominationModal from './components/HRBPDetails/ReviewNominationModal';
import HRBPDetailsCard from './components/HRBPDetails/HRBPDetailsCard';
import ActionTabs from '../../component/Tabs/Tabs'; 
import PendingNominationsTable from './components/HRBPDetails/TablePendingNomination';
import ApprovedNominationsTable from './components/HRBPDetails/TableApprovedNomination';
import RejectedNominationsTable from './components/HRBPDetails/TableRejectedNomination';
import SearchBar from '../../component/SearchBar/SearchBar';
import * as XLSX from "xlsx";
import "./RewardsandRecognition.scss";

interface Filters{
    [key: string]: string[];
}

const MONTH_ORDER = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const HRBPTable = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedNomination, setSelectedNomination] = useState(null);
    const [pendingNominations, setPendingNominations] = useState([]);
    const [approvedNominations, setApprovedNominations] = useState([]);
    const [rejectedNominations, setRejectedNominations] = useState([]);
    const [activeTab, setActiveTab] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState<Filters>({});
    const [pagination, setPagination] = useState({
        pending: { currentPage: 1, itemsPerPage: 5 },
        approved: { currentPage: 1, itemsPerPage: 5 },
        rejected: { currentPage: 1, itemsPerPage: 5 },
    });
    const [isDownloading, setIsDownloading] = useState(false);
    const role = localStorage.getItem("role");
    const isHR = role?.includes("hr") || role?.includes("employee-db-hrmanager") || role?.includes("employee-db-hrbp") || role?.includes("hr-ta");

    const fetchAllNominations = () => {
        setIsLoading(true);

        Promise.all([
            new Promise((resolve) => {
                dispatch(actions.listNominationsAction({ status: 'Pending' }, resolve));
            }),
            new Promise((resolve) => {
                dispatch(actions.listNominationsAction({ status: 'Approved' }, resolve));
            }),
            new Promise((resolve) => {
                dispatch(actions.listNominationsAction({ status: 'Rejected' }, resolve));
            })
        ]).then(([pendingRes, approvedRes, rejectedRes]: any) => {
            if (pendingRes.success) {
                setPendingNominations(pendingRes.data || []);
                showSuccessToastMessage('Pending nominations fetched successfully.');
            } else {
                showErrorToastMessage('Failed to fetch pending nominations.');
            }
            if (approvedRes.success) {
                setApprovedNominations(approvedRes.data || []);
                showSuccessToastMessage('Approved nominations fetched successfully.');
            } else {
                showErrorToastMessage('Failed to fetch approved nominations.');
            }
            if(rejectedRes.success) {
                setRejectedNominations(rejectedRes.data || []);
                showSuccessToastMessage('Rejected nominations fetched successfully.');
            }
            else{
                showErrorToastMessage('Failed to fetch rejected nominations.');
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchAllNominations();
    }, [dispatch, isHR]);

    const filterOptions = useMemo(() => {
        const allNominations = [...pendingNominations, ...approvedNominations, ...rejectedNominations];
        const options: { [key: string]: Set<string> } = {
            awardCategory: new Set(),
            employeeStatus: new Set(),
            employeeDesignation: new Set(),
            employeeDepartment: new Set(),
            nominatedBy: new Set(),
            reviewedBy: new Set(),
            nominationMonth: new Set(),
            nominationYear: new Set(),
            reviewedMonth: new Set(),
            reviewedYear: new Set(),
        };

        allNominations.forEach((nom: any) => {
            // if (nom.awardRef?.awardCategory) options.awardCategory.add(nom.awardRef.awardCategory);
            // if (nom.nominatedByRef?.candidateName) options.nominatedBy.add(`${nom.nominatedByRef.candidateName} ${nom.nominatedByRef.lastname}`);
            // if (nom.reviewedByRef?.candidateName) options.reviewedBy.add(`${nom.reviewedByRef.candidateName} ${nom.reviewedByRef.lastname}`);
            if (nom.awardRef?.awardCategory) options.awardCategory.add(nom.awardRef.awardCategory);
            if (nom.employeeRef?.status) options.employeeStatus.add(nom.employeeRef.status);
            if (nom.employeeRef?.designation) options.employeeDesignation.add(nom.employeeRef.designation);
            if (nom.employeeRef?.department) options.employeeDepartment.add(`${nom.employeeRef.department} - ${nom.employeeRef.subDepartment || ''}`);
            if (nom.nominatedByRef?.candidateName) options.nominatedBy.add(`${nom.nominatedByRef.candidateName} ${nom.nominatedByRef.lastname || ''}`.trim());
            if (nom.reviewedByRef?.candidateName) options.reviewedBy.add(`${nom.reviewedByRef.candidateName} ${nom.reviewedByRef.lastname || ''}`.trim());
            if (nom.createdAt) {
                const createdDate = new Date(nom.createdAt);
                options.nominationMonth.add(createdDate.toLocaleString('default', { month: 'long' }));
                options.nominationYear.add(createdDate.getUTCFullYear().toString());
            }
            if (nom.reviewedAt) {
                const reviewedDate = new Date(nom.reviewedAt);
                options.reviewedMonth.add(reviewedDate.toLocaleString('default', { month: 'long' }));
                options.reviewedYear.add(reviewedDate.getUTCFullYear().toString());
            }
        });

        return {
            // awardCategory: Array.from(options.awardCategory).sort(),
            // nominatedBy: Array.from(options.nominatedBy).sort(),
            // reviewedBy: Array.from(options.reviewedBy).sort(),
            awardCategory: Array.from(options.awardCategory).sort(),
            employeeStatus: Array.from(options.employeeStatus).sort(),
            employeeDesignation: Array.from(options.employeeDesignation).sort(),
            employeeDepartment: Array.from(options.employeeDepartment).sort(),
            nominatedBy: Array.from(options.nominatedBy).sort(),
            reviewedBy: Array.from(options.reviewedBy).sort(),
            nominationMonth: Array.from(options.nominationMonth).sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b)),
            nominationYear: Array.from(options.nominationYear).sort((a, b) => parseInt(b) - parseInt(a)),
            reviewedMonth: Array.from(options.reviewedMonth).sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b)),
            reviewedYear: Array.from(options.reviewedYear).sort((a, b) => parseInt(b) - parseInt(a)),
        };
    }, [pendingNominations, approvedNominations, rejectedNominations]);

    const processNominations = (nominations: any[]) => {
        let result = nominations;
        
        Object.entries(filters).forEach(([field, values]) => {
            if (values.length > 0) {
                result = result.filter(nom => {
                //     let fieldValue = '';
                //     if (field === 'awardCategory') fieldValue = nom.awardRef?.awardCategory;
                //     if (field === 'employeeStatus') fieldValue = nom.employeeRef?.status;
                //     if (field === 'employeeDesignation') fieldValue = nom.employeeRef?.designation;
                //     if (field === 'employeeDepartment') fieldValue = `${nom.employeeRef?.department || ''} - ${nom.employeeRef?.subDepartment || ''}`;
                //     if (field === 'nominatedBy') fieldValue = `${nom.nominatedByRef?.candidateName} ${nom.nominatedByRef?.lastname}`;
                //     if (field === 'reviewedBy') fieldValue = `${nom.reviewedByRef?.candidateName} ${nom.reviewedByRef?.lastname}`;
                //     if (field === 'nominationDate') fieldValue = nom.createdAt ? new Date(nom.createdAt).toLocaleDateString() : '';
                //     if (field === 'reviewedOn') fieldValue = nom.reviewedAt ? new Date(nom.reviewedAt).toLocaleDateString() : '';
                //     if (field === 'status') fieldValue = nom.status;
                //     return values.includes(fieldValue);
                // });
                let fieldValue: string | undefined;

                    // Handle date fields 
                    if (field === 'nominationMonth' && nom.createdAt) {
                        fieldValue = new Date(nom.createdAt).toLocaleString('default', { month: 'long' });
                    } else if (field === 'nominationYear' && nom.createdAt) {
                        fieldValue = new Date(nom.createdAt).getUTCFullYear().toString();
                    } else if (field === 'reviewedMonth' && nom.reviewedAt) {
                        fieldValue = new Date(nom.reviewedAt).toLocaleString('default', { month: 'long' });
                    } else if (field === 'reviewedYear' && nom.reviewedAt) {
                        fieldValue = new Date(nom.reviewedAt).getUTCFullYear().toString();
                    } 
                    // Handle other string fields
                    else {
                        const fieldMap: { [key: string]: string | undefined } = {
                            'awardCategory': nom.awardRef?.awardCategory,
                            'employeeStatus': nom.employeeRef?.status,
                            'employeeDesignation': nom.employeeRef?.designation,
                            'employeeDepartment': `${nom.employeeRef?.department || ''} - ${nom.employeeRef?.subDepartment || ''}`,
                            'nominatedBy': `${nom.nominatedByRef?.candidateName || ''} ${nom.nominatedByRef?.lastname || ''}`.trim(),
                            'reviewedBy': `${nom.reviewedByRef?.candidateName || ''} ${nom.reviewedByRef?.lastname || ''}`.trim(),
                        };
                        fieldValue = fieldMap[field];
                    }

                    return fieldValue ? values.includes(fieldValue) : false;
                });
            }
        });

        // Apply search
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            result = result.filter(nom => 
                (nom.employeeRef?.candidateName + " " + nom.employeeRef?.lastname).toLowerCase().includes(lowerCaseQuery) ||
                (nom.awardRef?.awardCategory).toLowerCase().includes(lowerCaseQuery) ||
                (nom.nominatedByRef?.candidateName + " " + nom.nominatedByRef?.lastname).toLowerCase().includes(lowerCaseQuery) ||
                (nom.reviewedByRef?.candidateName + " " + nom.reviewedByRef?.lastname).toLowerCase().includes(lowerCaseQuery) ||
                (nom.status).toLowerCase().includes(lowerCaseQuery)
            );
        }
        return result;
    };

    const processedPending = useMemo(() => processNominations(pendingNominations), [pendingNominations, filters, searchQuery]);
    const processedApproved = useMemo(() => processNominations(approvedNominations), [approvedNominations, filters, searchQuery]);
    const processedRejected = useMemo(() => processNominations(rejectedNominations), [rejectedNominations, filters, searchQuery]);

    // Pagination slicing for each list ---
    const paginate = (items: any[], page: number, itemsPerPage: number) => {
        const indexOfLastItem = page * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return items.slice(indexOfFirstItem, indexOfLastItem);
    };

    const currentPending = paginate(processedPending, pagination.pending.currentPage, pagination.pending.itemsPerPage);
    const currentApproved = paginate(processedApproved, pagination.approved.currentPage, pagination.approved.itemsPerPage);
    const currentRejected = paginate(processedRejected, pagination.rejected.currentPage, pagination.rejected.itemsPerPage);

    const totalPendingPages = Math.ceil(processedPending.length / pagination.pending.itemsPerPage);
    const totalApprovedPages = Math.ceil(processedApproved.length / pagination.approved.itemsPerPage);
    const totalRejectedPages = Math.ceil(processedRejected.length / pagination.rejected.itemsPerPage);

    // Handlers for state updates ---
    const handlePageChange = (status: 'pending' | 'approved' | 'rejected', pageNumber: number) => {
        setPagination(prev => ({
            ...prev,
            [status]: { ...prev[status], currentPage: pageNumber },
        }));
    };

    const handleFilterChange = (field: string, value: string) => {
        setFilters(prevFilters => {
        const currentValues = prevFilters[field] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value) 
            : [...currentValues, value]; 
        if (newValues.length === 0) {
            const { [field]: _, ...rest } = prevFilters;
            return rest; 
        }
        
        return { ...prevFilters, [field]: newValues };
        });
    };
    
    const removeFilter = (filterToRemove: { field: string, value: string }) => {
        handleFilterChange(filterToRemove.field, filterToRemove.value);
    };

    const activeFiltersForDisplay = Object.entries(filters).flatMap(([field, values]) => 
        values.map(value => ({ title: `${field.replace(/([A-Z])/g, ' $1')}: ${value}`, value, field }))
    );

    // Reset all pages to 1 when filters change
    useEffect(() => {
        setPagination(prev => ({
            pending: { ...prev.pending, currentPage: 1 },
            approved: { ...prev.approved, currentPage: 1 },
            rejected: { ...prev.rejected, currentPage: 1 },
        }));
    }, [searchQuery, filters]);

    const handleOpenModal = (nomination: any) => {
        setSelectedNomination(nomination);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedNomination(null);
    };

    const handleDownloadExcel = () => {
        setIsDownloading(true);

        const workbook = XLSX.utils.book_new();

        // Pending Nominations
        const pendingData = processedPending.map(nom => ({
            'Employee ID': nom.employeeRef?.employeeId || 'N/A',
            'Employee Name': `${nom.employeeRef?.candidateName || ''} ${nom.employeeRef?.lastname || ''}`.trim(),
            'Employee E-Mail ID': nom.employeeRef?.emailId || 'N/A',
            'Employee Status': nom.employeeRef?.status || 'N/A',
            'Employee Designation': nom.employeeRef?.designation || 'N/A',
            'Employee Department': `${nom.employeeRef?.department || ''} - ${nom.employeeRef?.subDepartment || ''}`,
            'Employee Project Classification': `${nom.employeeRef?.projectType || ''} - ${nom.employeeRef?.projectClassification || ''}`,
            'Nominated By': `${nom.nominatedByRef?.candidateName || ''} ${nom.nominatedByRef?.lastname || ''}`.trim(),
            'Award Category': nom.awardRef?.awardCategory || 'N/A',
            'Nomination Date': nom.createdAt ? new Date(nom.createdAt).toLocaleDateString() : 'N/A',
            'Nomination Reason': nom.justification || 'N/A',
            'Status': nom.status,
        }));
        const pendingWs = XLSX.utils.json_to_sheet(pendingData);
        XLSX.utils.book_append_sheet(workbook, pendingWs, "Pending Nominations");

        // Approved Nominations
        const approvedData = processedApproved.map(nom => ({
            'Employee ID': nom.employeeRef?.employeeId || 'N/A',
            'Employee Name': `${nom.employeeRef?.candidateName || ''} ${nom.employeeRef?.lastname || ''}`.trim(),
            'Employee E-Mail ID': nom.employeeRef?.emailId || 'N/A',
            'Employee Status': nom.employeeRef?.status || 'N/A',
            'Employee Designation': nom.employeeRef?.designation || 'N/A',
            'Employee Department': `${nom.employeeRef?.department || ''} - ${nom.employeeRef?.subDepartment || ''}`,
            'Employee Project Classification': `${nom.employeeRef?.projectType || ''} - ${nom.employeeRef?.projectClassification || ''}`,
            'Nominated By': `${nom.nominatedByRef?.candidateName || ''} ${nom.nominatedByRef?.lastname || ''}`.trim(),
            'Award Category': nom.awardRef?.awardCategory || 'N/A',
            'Nominated On': nom.createdAt ? new Date(nom.createdAt).toLocaleDateString() : 'N/A',
            'Nomination Reason': nom.justification || 'N/A',
            'Reviewed By (HR/HRBP)': `${nom.reviewedByRef?.candidateName || ''} ${nom.reviewedByRef?.lastname || 'N/A'}`.trim(),
            'Reviewed On': nom.reviewedAt ? new Date(nom.reviewedAt).toLocaleDateString() : 'N/A',
            'Approval Review Remarks': nom.hrbpRemarks || 'N/A',
            'Status': nom.status,
        }));
        const approvedWs = XLSX.utils.json_to_sheet(approvedData);
        XLSX.utils.book_append_sheet(workbook, approvedWs, "Approved Nominations");

        // Rejected Nominations ---
        const rejectedData = processedRejected.map(nom => ({
            'Employee ID': nom.employeeRef?.employeeId || 'N/A',
            'Employee Name': `${nom.employeeRef?.candidateName || ''} ${nom.employeeRef?.lastname || ''}`.trim(),
            'Employee E-Mail ID': nom.employeeRef?.emailId || 'N/A',
            'Employee Status': nom.employeeRef?.status || 'N/A',
            'Employee Designation': nom.employeeRef?.designation || 'N/A',
            'Employee Department': `${nom.employeeRef?.department || ''} - ${nom.employeeRef?.subDepartment || ''}`,
            'Employee Project Classification': `${nom.employeeRef?.projectType || ''} - ${nom.employeeRef?.projectClassification || ''}`,
            'Nominated By': `${nom.nominatedByRef?.candidateName || ''} ${nom.nominatedByRef?.lastname || ''}`.trim(),
            'Award Category': nom.awardRef?.awardCategory || 'N/A',
            'Nominated On': nom.createdAt ? new Date(nom.createdAt).toLocaleDateString() : 'N/A',
            'Nomination Reason': nom.justification || 'N/A',
            'Reviewed By (HR/HRBP)': `${nom.reviewedByRef?.candidateName || ''} ${nom.reviewedByRef?.lastname || 'N/A'}`.trim(),
            'Reviewed On': nom.reviewedAt ? new Date(nom.reviewedAt).toLocaleDateString() : 'N/A',
            'Rejection Review Remarks': nom.hrbpRemarks || 'N/A',
            'Status': nom.status,
        }));
        const rejectedWs = XLSX.utils.json_to_sheet(rejectedData);
        XLSX.utils.book_append_sheet(workbook, rejectedWs, "Rejected Nominations");

        XLSX.writeFile(workbook, 'All_Nominations_Report.xlsx');
        
        setIsDownloading(false);
    };

    if(!isHR){
        return (
            <div>
                <MainCard>
                    <div className='title-card mb-4'>
                        <h2>Access Denied</h2>
                    </div>
                </MainCard>
            </div>
        )
    }

    return (
        <div>
            <MainCard>
                <div className='title-card mb-4'>
                    <h2>Nomination Review Dashboard</h2>
                </div>

                <div className="mb-4">
                    <HRBPDetailsCard />
                </div>

                {/* Unified Search and Filter Section */}
                <div className="m-2">
                    <p>Search and Filter</p>
                    <div className="mt-3 buttonsize filter-wrapper">
                        <div className="emp-searchbar-wrapper">
                            <SearchBar
                                filter={searchQuery}
                                setFilter={setSearchQuery}
                                onSearchClicked={() => {}}
                                placeholderName="Search by Employee or Award..."
                            />
                        </div>
                        <div className="emp-filter-container">
                            <Dropdown autoClose="outside">
                                <Dropdown.Toggle variant="light" id="dropdown-filter-hrbp">
                                    Filters
                                    {activeFiltersForDisplay.length > 0 && (
                                        <Badge bg="primary" pill className="ms-2">{activeFiltersForDisplay.length}</Badge>
                                    )}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto', width: '300px' }}>
                                    {activeFiltersForDisplay.length > 0 && (
                                        <>
                                            <Dropdown.Item onClick={() => setFilters({})}>
                                                <span style={{ color: 'red' }}>Clear All Filters</span>
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                        </>
                                    )}
                                    {Object.keys(filterOptions).map(field => (
                                        filterOptions[field as keyof typeof filterOptions].length > 0 && (
                                            <React.Fragment key={field}>
                                                <Dropdown.Header>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Dropdown.Header>
                                                {filterOptions[field as keyof typeof filterOptions].map(option => (
                                                    <Dropdown.Item key={option} onClick={() => handleFilterChange(field, option)}>
                                                        <Form.Check type="checkbox" label={option} checked={(filters[field] || []).includes(option)} readOnly />
                                                    </Dropdown.Item>
                                                ))}
                                            </React.Fragment>
                                        )
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>


                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '25px',
                    }}>
                        <FilterValues 
                            filterValueArray={activeFiltersForDisplay} 
                            onFilterChange={removeFilter}
                        />

                        <Button
                            variant="outline-primary"
                            onClick={handleDownloadExcel}
                            disabled={isDownloading}
                            className="ms-2"
                        >
                            {isDownloading ? (
                                <>
                                    <Spinner 
                                        animation="border" 
                                        size="sm" 
                                        role="status" 
                                        aria-hidden="true" 
                                    />
                                        <span className="ms-2">Downloading...</span>
                                    </>
                                ) : (
                                    `Download Nominations Report`
                                )}
                        </Button>
                    </div>
                    
                </div>

                <div className="profile-tabs-container mb-4">
                    <ActionTabs
                        title="Pending Requests"
                        onClick={() => setActiveTab('pending')}
                        active={activeTab === 'pending'}
                    />
                    <ActionTabs
                        title="Approved History"
                        onClick={() => setActiveTab('approved')}
                        active={activeTab === 'approved'}
                    />
                    <ActionTabs
                        title="Rejected History"
                        onClick={() => setActiveTab('rejected')}
                        active={activeTab === 'rejected'}
                    />
                </div>


                {isLoading ? (
                    <div className="text-center p-5">
                        <span>Loading tables...</span>
                    </div>
                ) : (
                    <div className="profile-containers">
                        {activeTab === 'pending' && (
                            <>
                                <PendingNominationsTable 
                                    nominations={currentPending} 
                                    onReviewClick={handleOpenModal} 
                                />
                                {totalPendingPages > 1 && (
                                    <TablePagination
                                        totalPages={totalPendingPages}
                                        currentPage={pagination.pending.currentPage}
                                        onClick={(page: number) => handlePageChange('pending', page)}
                                    />
                                )}
                            </>
                        )}
                        {activeTab === 'approved' && (
                            <>
                                <ApprovedNominationsTable nominations={currentApproved} />
                                {totalApprovedPages > 1 && (
                                    <TablePagination
                                        totalPages={totalApprovedPages}
                                        currentPage={pagination.approved.currentPage}
                                        onClick={(page: number) => handlePageChange('approved', page)}
                                    />
                                )}
                            </>
                        )}
                        {activeTab === 'rejected' && (
                            <>
                                <RejectedNominationsTable nominations={currentRejected} />
                                {totalRejectedPages > 1 && (
                                    <TablePagination
                                        totalPages={totalRejectedPages}
                                        currentPage={pagination.rejected.currentPage}
                                        onClick={(page: number) => handlePageChange('rejected', page)}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}

                {selectedNomination && (
                    <ReviewNominationModal
                        show={showModal}
                        onHide={handleCloseModal}
                        nomination={selectedNomination}
                        onSuccess={fetchAllNominations}
                    />
                )}
            </MainCard>
        </div>
    );
}

export default HRBPTable;